import {useCallback, useRef} from 'react'
import debounce from 'lodash/debounce.js'
import classNames from 'classnames'

import apiverson from '../apiverson.js'
import {getState, updateForm, formsSelector, useAutoForm} from '../../store.js'
import {setOrder} from '../../data/orders.js'
import Icon from '../icons/Icon.js'
import TextInput from './TextInput.js'
import Zropdown, {useZropdown} from './Zropdown.js'
import OrderStatus from '../../ordoro/OrderListPage/Fields/OrderStatus.js'

export function getInitialForm() {
  return {
    text: '',
    results: [],
    selectedIndex: 0,
    hasFoundNothing: false,
    isLoading: false,
    serverError: null,
  }
}

export async function searchOrders(
  dropdown,
  excludedOrderNumbers = [],
  queryParams = {},
) {
  const taskQueue = searchOrders.taskQueue || {}
  searchOrders.taskQueue = taskQueue

  taskQueue[dropdown] = {}

  try {
    const form = formsSelector(getState())[dropdown]

    if (!form) {
      return
    }

    const text = form.text.trim()

    if (!text) {
      updateForm(dropdown, {
        results: [],
        selectedIndex: 0,
        hasFoundNothing: false,
        isLoading: false,
        serverError: null,
      })

      return
    }

    updateForm(dropdown, {isLoading: true, serverError: null})

    const limit = 15 + excludedOrderNumbers.length

    const {
      json: {order: orders},
    } = await apiverson.get('/order', {
      limit,
      search: form.text,
      ...queryParams,
    })

    if (searchOrders.taskQueue[dropdown] !== taskQueue[dropdown]) {
      return
    }

    const results = orders.filter(
      ({order_number}) => !excludedOrderNumbers.includes(order_number),
    )

    updateForm(dropdown, {
      results,
      selectedIndex: 0,
      hasFoundNothing: results.length === 0,
      isLoading: false,
    })
  } catch (err) {
    updateForm(dropdown, {
      results: [],
      selectedIndex: 0,
      serverError: err.message,
      isLoading: false,
    })
  } finally {
    if (searchOrders.taskQueue[dropdown] === taskQueue[dropdown]) {
      delete taskQueue[dropdown]
    }
  }
}

/**
 * Search filter for Orders
 *
 * @param {Object} props
 * @param {String|Function} [props.label]
 * @param {String} [props.placeholder]
 * @param {String} props.dropdown
 * @param {Function} props.onSelect
 * @param {String[]} [props.excludedOrderNumbers]
 * @param {Object} [props.queryParams]
 * @param {Boolean} [props.autoFocus]
 * @param {Function} [props.onBlur]
 * @returns {React.ReactNode}
 */
export default function OrderFilter({
  label,
  placeholder,
  dropdown,
  onSelect,
  excludedOrderNumbers,
  queryParams,
  autoFocus,
  onBlur,
}) {
  const id = `${dropdown}__input`
  placeholder = placeholder || 'Search for an order ID...'

  const debouncedSearchOrders = useCallback(
    debounce(
      () => searchOrders(dropdown, excludedOrderNumbers, queryParams),
      400,
    ),
    [dropdown, excludedOrderNumbers, queryParams],
  )

  const ref = useRef()

  const form = useAutoForm(dropdown, getInitialForm())

  const {onChangeInternal, onSelectInternal, onConfirm, onUp, onDown} =
    useZropdown({
      dropdown,
      form,
      onChange: () => debouncedSearchOrders(),
      onSelect: (order) => {
        setOrder(order)

        onSelect(order)

        onChangeInternal('')
      },
    })

  return (
    <div className="outerwrap--order-search-zropdown">
      {label && (
        <label className="label--order-search" htmlFor={id}>
          {label}
        </label>
      )}
      <div className="wrap--order-search" ref={ref}>
        <label className="label--input-prefix label--search" htmlFor={id}>
          <Icon name="search" className="icon--input-prefix" />
          <TextInput
            className="input--search input--lighter inline-block v-align-middle"
            id={id}
            value={form.text}
            placeholder={placeholder}
            onChange={(value) => onChangeInternal(value)}
            onBlur={(event) => onBlur && onBlur(event)}
            autoFocus={autoFocus}
          />
        </label>
      </div>
      <Zropdown
        parentRef={ref}
        dropdown={dropdown}
        form={form}
        onConfirm={onConfirm}
        onUp={onUp}
        onDown={onDown}
      >
        {form.isLoading && (
          <li className="list__item--dropdown list__item--dropdown-empty align-center">
            <span className="spinner--sm v-align-middle" />
            <strong className="inline-block v-align-middle fs-00 op-75 margin-left-5">
              Loading...
            </strong>
          </li>
        )}
        {form.results.map((order, index) => (
          <li key={order.id} className="list__item list__item--dropdown">
            <button
              className={classNames(
                'btn--link list__link list__link--dropdown flex--justify-nowrap',
                {
                  'list__link--dropdown-arrow-focus':
                    index === form.selectedIndex,
                },
              )}
              onClick={() => onSelectInternal(order)}
            >
              <div>
                <strong className="block">{order.order_number}</strong>
                <span className="block unbold">
                  Status: <OrderStatus order={order} />
                </span>
              </div>
            </button>
          </li>
        ))}
        {form.hasFoundNothing && (
          <li className="list__item--dropdown list__item--dropdown-empty align-center">
            <em>No orders were found</em>
          </li>
        )}
      </Zropdown>
    </div>
  )
}
